import { useState } from 'react';
import { Link, useLocation } from 'wouter';
import { cn } from '@/lib/utils';
import {
  Activity,
  Award,
  Building,
  Building2,
  Briefcase,
  Calendar,
  CalendarDays,
  CheckSquare,
  Home,
  Lightbulb,
  Mail,
  Menu,
  Settings,
  Sparkles,
  Target,
  Trash2,
  Users,
  Handshake,
  HelpCircle,
  Mic,
} from 'lucide-react';
import { openQuickNoteComposer } from '@/lib/noteEntityContext';

interface BottomNavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

const NAV_ITEMS: BottomNavItem[] = [
  { label: 'Contatti', path: '/contacts', icon: <Users className="h-5 w-5" /> },
  { label: 'Aziende', path: '/companies', icon: <Building2 className="h-5 w-5" /> },
  { label: 'Deal', path: '/deals', icon: <Briefcase className="h-5 w-5" /> },
  { label: 'Email', path: '/emails', icon: <Mail className="h-5 w-5" /> },
];

const MORE_GROUPS: Array<{ label: string; items: BottomNavItem[] }> = [
  {
    label: 'Core',
    items: [
      { label: 'Dashboard', path: '/', icon: <Home className="h-5 w-5" /> },
      { label: 'Contatti', path: '/contacts', icon: <Users className="h-5 w-5" /> },
      { label: 'Aziende', path: '/companies', icon: <Building2 className="h-5 w-5" /> },
      { label: 'Filiali', path: '/branches', icon: <Building className="h-5 w-5" /> },
      { label: 'Brands', path: '/brands', icon: <Award className="h-5 w-5" /> },
      { label: 'Opportunità', path: '/deals', icon: <Briefcase className="h-5 w-5" /> },
      { label: 'Opportunità AI', path: '/opportunities', icon: <Sparkles className="h-5 w-5" /> },
      { label: 'Lead', path: '/leads', icon: <Target className="h-5 w-5" /> },
    ],
  },
  {
    label: 'Relazioni',
    items: [
      { label: 'Sinergie', path: '/synergies', icon: <Handshake className="h-5 w-5" /> },
      { label: 'Associazioni', path: '/associations', icon: <Activity className="h-5 w-5" /> },
      { label: 'Import Sicuro', path: '/import-export-module', icon: <Lightbulb className="h-5 w-5" /> },
    ],
  },
  {
    label: 'Comunicazione',
    items: [
      { label: 'Email', path: '/emails', icon: <Mail className="h-5 w-5" /> },
      { label: 'Task', path: '/tasks', icon: <CheckSquare className="h-5 w-5" /> },
      { label: 'Meeting', path: '/meetings', icon: <Calendar className="h-5 w-5" /> },
      { label: 'Attività', path: '/activities', icon: <CheckSquare className="h-5 w-5" /> },
      { label: 'Calendario', path: '/calendar', icon: <CalendarDays className="h-5 w-5" /> },
    ],
  },
  {
    label: 'Sistema',
    items: [
      { label: 'Deduplicazione', path: '/deduplication', icon: <Trash2 className="h-5 w-5" /> },
      { label: 'Impostazioni', path: '/settings', icon: <Settings className="h-5 w-5" /> },
      { label: 'Aiuto', path: '/help', icon: <HelpCircle className="h-5 w-5" /> },
      { label: 'Manutenzione', path: '/admin/maintenance', icon: <Trash2 className="h-5 w-5" /> },
    ],
  },
];

export default function MobileBottomNav({ onMenuOpen }: { onMenuOpen: () => void }) {
  const [location] = useLocation();
  const [showMore, setShowMore] = useState(false);

  const isActive = (path: string) => {
    if (path === '/') return location === '/' || location === '/dashboard';
    return location === path || location.startsWith(path + '/');
  };

  return (
    <>
      {/* Bottom Navigation Bar */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 bg-background border-t border-border md:hidden safe-area-pb">
        <div className="flex items-center justify-around h-14">
          {NAV_ITEMS.map((item) => (
            <div key={item.path} className="contents">
              <Link href={item.path} className="h-full min-w-0 flex-1">
                <div
                  className={cn(
                    'flex h-full min-w-0 flex-col items-center justify-center gap-0.5 cursor-pointer',
                    isActive(item.path)
                      ? 'text-primary'
                      : 'text-muted-foreground'
                  )}
                >
                  {item.icon}
                  <span className="max-w-full truncate text-xs font-medium leading-3">{item.label}</span>
                </div>
              </Link>
              {item.path === '/deals' && (
                <button
                  type="button"
                  data-testid="mobile-nav-quick-note"
                  onClick={() => openQuickNoteComposer({ startVoice: true })}
                  className="flex h-full min-w-0 flex-1 flex-col items-center justify-center gap-0.5 text-muted-foreground"
                  aria-label="Detta una nota rapida"
                >
                  <Mic className="h-5 w-5" />
                  <span className="max-w-full truncate text-xs font-medium leading-3">Detta nota</span>
                </button>
              )}
            </div>
          ))}
          {/* Menu button */}
          <button
            onClick={() => setShowMore(!showMore)}
            className={cn(
              'flex flex-col items-center justify-center flex-1 h-full gap-0.5',
              showMore ? 'text-primary' : 'text-muted-foreground'
            )}
          >
            <Menu className="h-5 w-5" />
            <span className="text-xs font-medium">Menu</span>
          </button>
        </div>
      </nav>

      {/* More menu overlay */}
      {showMore && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-50 md:hidden"
            onClick={() => setShowMore(false)}
          />
          <div className="fixed bottom-14 left-0 right-0 z-50 max-h-[70vh] overflow-y-auto border-t border-border bg-background shadow-xl md:hidden">
            <div className="space-y-4 p-4">
              {MORE_GROUPS.map((group) => (
                <section key={group.label} className="space-y-2">
                  <h3 className="px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {group.label}
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    {group.items.map((item) => (
                      <Link key={item.path} href={item.path}>
                        <div
                          onClick={() => setShowMore(false)}
                          className={cn(
                            'flex min-w-0 items-center gap-2 border border-border p-3 cursor-pointer',
                            isActive(item.path)
                              ? 'bg-primary/10 text-primary border-primary/30'
                              : 'text-muted-foreground hover:bg-accent'
                          )}
                        >
                          <span className="shrink-0">{item.icon}</span>
                          <span className="truncate text-xs font-medium">{item.label}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
}
